import { useMemo, type CSSProperties } from 'react';
import { Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { formatJsonValue } from '~/app/utils/json';
import { highlightJsonLine } from './assetLogJsonHighlight';
import styles from '~/app/components/asset-detail/AssetLogsTable/AssetLogsTable.module.css';

const { Text } = Typography;

type Props = {
  value: unknown;
  maxHeight?: number;
};

type JsonViewerStyle = CSSProperties & {
  '--log-json-gutter'?: string;
};

export function AssetLogJsonPayload({ value, maxHeight = 320 }: Props) {
  const { t } = useTranslation('assets');

  const lines = useMemo(() => {
    if (value === null || value === undefined) {
      return null;
    }

    const formatted = formatJsonValue(value, true);
    if (!formatted) {
      return null;
    }

    return formatted.split('\n');
  }, [value]);

  if (!lines) {
    return (
      <Text type="secondary" className={styles.jsonEmpty}>
        {t('detail.logs.emptyPayload', 'No payload available.')}
      </Text>
    );
  }

  const viewerStyle: JsonViewerStyle = {
    maxHeight,
    '--log-json-gutter': `${String(lines.length).length + 1}ch`,
  };

  return (
    <pre className={styles.jsonViewer} style={viewerStyle}>
      <code className={styles.jsonCode}>
        {lines.map((line, index) => (
          <div key={`line-${index + 1}`} className={styles.jsonLine}>
            <span className={styles.jsonLineNumber} aria-hidden>
              {index + 1}
            </span>
            <span className={styles.jsonLineContent}>
              {highlightJsonLine(line)}
            </span>
          </div>
        ))}
      </code>
    </pre>
  );
}
